import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    Share2, Users, Eye, Pencil, Trash2, Loader2, Plus, Scissors,
    ArrowDownLeft, ArrowUpRight, ShieldCheck, ArrowLeftRight, BadgeCheck, Check,
} from 'lucide-react';
import {
    getMyFamily, getShares, updateShare, revokeShare, switchProfile,
} from '../../redux/familySlice';
import AddFamilyMember from '../family/AddFamilyMember';
import SplitMemberFlow from '../family/SplitMemberFlow';
import { getFileURL } from '../../utils/fileURL';

// DEPENDENCIES — everything about who this account is connected to.
//
// This tab used to manage `otherContact`: a free-text list of emergency contacts
// stored as JSON on the patient row, edited through updatePatient with
// keypass 'adddependency' / 'editdependency' / 'deletedependency'. That was a
// notes field pretending to be a relationship — nobody in it could be reached,
// booked for, or verified, and it had no bearing on who could open the record.
//
// It now holds the two REAL relationships, which the patient controls in full:
//
//   SECONDARY ACCOUNTS  people who belong to this account and cannot sign in.
//                       Add them here, and split them into their own account.
//   SHARING             who can open THIS account, and whose accounts this one
//                       can open — each at Read or Read & write.
//
// BOTH ARE ACCOUNT-LEVEL, NOT HOSPITAL-LEVEL (doc/one-patient-row.md §2). A
// family member is one person row wherever they are treated, and a share is a
// global edge; a hospital simply sees the edges whose BOTH ends hold a patient
// number there. So nothing on this screen is per-hospital, and nothing here is
// duplicated by visiting a second one.
//
// The header menu carries short versions of both. This is the detailed screen:
// the one place where all of it is visible together.
//
// THE STORED `otherContact` DATA IS UNTOUCHED. Removing a screen is reversible;
// deleting somebody's contact records is not, so that is a separate decision
// taken deliberately rather than as a side effect of this change.
const Dependencies = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const {
        primary, members, enabled, sharedWithOthers, sharedWithMe, sharesLoading, loading,
    } = useSelector((s) => s.family || {});

    const [addOpen, setAddOpen] = useState(false);
    const [splitFor, setSplitFor] = useState(null);
    const [busy, setBusy] = useState(null);
    const [confirming, setConfirming] = useState(null);
    const [switching, setSwitching] = useState(null);

    // Staff open this tab on somebody else's profile; a patient opens their own.
    // Only the patient may change any of it — these are decisions about their own
    // records, and the server enforces that whatever renders here.
    const userRole = localStorage.getItem('Role');
    const isOwnProfile = userRole === 'patient';

    useEffect(() => {
        if (!isOwnProfile) return;
        dispatch(getMyFamily());
        dispatch(getShares());
    }, [dispatch, isOwnProfile]);

    const people = [primary, ...(members || [])].filter(Boolean);
    const secondaries = people.filter((p) => p.shareSource === 'secondary');
    const activeId = localStorage.getItem('ID');

    // Switching re-issues the TOKEN server-side — it is not a screen-only
    // change, or anyone could ask for anyone's records. The reload afterwards
    // makes every cached list re-fetch as the new person; leaving stale data on
    // screen is how somebody books against the wrong medical record.
    const switchTo = async (patientId) => {
        if (patientId === activeId) return;
        setSwitching(patientId);
        try {
            await dispatch(switchProfile(patientId)).unwrap();
            window.location.reload();
        } catch (err) {
            const msg = err?.en || err?.message?.en || (typeof err === 'string' ? err : null);
            toast.error(msg || t('family.switch_failed', 'Could not switch profile'));
            setSwitching(null);
        }
    };

    const act = async (thunk, arg, okMsg) => {
        setBusy(arg?.shareId || arg);
        setConfirming(null);
        try {
            const res = await dispatch(thunk(arg)).unwrap();
            toast.success(res?.message?.en || okMsg);
        } catch (err) {
            const msg = err?.en || err?.message?.en || (typeof err === 'string' ? err : null);
            toast.error(msg || t('family.action_failed', 'That did not work'));
        } finally { setBusy(null); }
    };

    const avatar = (p, size = 'h-9 w-9') => (
        p.image || p.imagePath
            ? <img src={getFileURL(p.image || p.imagePath)} alt="" className={`${size} flex-shrink-0 rounded-full object-cover`} />
            : (
                <span className={`${size} flex flex-shrink-0 items-center justify-center rounded-full bg-[#4ABDBD]/15 text-xs font-bold text-[#3AA8A8]`}>
                    {(p.name || '?').charAt(0).toUpperCase()}
                </span>
            )
    );

    const card = 'rounded-2xl border border-gray-200 bg-white';
    const sectionHead = 'flex items-center gap-2 border-b border-gray-100 px-5 py-3.5';

    if (!isOwnProfile) {
        return (
            <div className={`${card} p-6 text-center`}>
                <ShieldCheck className="mx-auto h-6 w-6 text-gray-300" />
                <p className="mt-2 text-sm font-semibold text-[#1a365d]">
                    {t('family.dep_patient_only', 'Only the patient can manage this')}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                    {t('family.dep_patient_only_note',
                        'Sharing and secondary accounts are the patient’s own decisions. Use the linked-account panel in the patient list to see who they are connected to.')}
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-5">

            {/* ---------- SWITCH PROFILE ----------
                First, because it answers "who am I acting as right now", and
                every list below is relative to that. */}
            <section className={card} data-testid="dep-switch">
                <div className={sectionHead}>
                    <ArrowLeftRight className="h-4 w-4 text-[#4ABDBD]" />
                    <h3 className="flex-1 text-sm font-bold text-[#1a365d]">
                        {t('family.switch_profile', 'Switch profile')}
                    </h3>
                </div>

                <p className="px-5 pt-3 text-[11px] leading-relaxed text-gray-500">
                    {t('family.dep_switch_note',
                        'Every account this sign-in can open. Switching changes who you are acting as across the whole app.')}
                </p>

                <div className="flex flex-col gap-1.5 px-5 py-3">
                    {loading && <Loader2 className="mx-auto h-4 w-4 animate-spin text-[#4ABDBD]" />}

                    {people.map((p) => {
                        const isActive = p.patientId === activeId;
                        const isOwner = p.patientId === primary?.patientId;
                        const kind = isOwner
                            ? t('family.primary', 'Primary')
                            : p.shareSource === 'secondary'
                                ? t('family.secondary', 'Secondary')
                                : t('family.shared', 'Shared');
                        return (
                            <button
                                key={p.patientId}
                                type="button"
                                data-testid={`dep-switch-${p.patientId}`}
                                disabled={!!switching || isActive}
                                onClick={() => switchTo(p.patientId)}
                                className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition disabled:opacity-100 ${
                                    isActive
                                        ? 'border-[#4ABDBD]/40 bg-[#4ABDBD]/10'
                                        : 'cursor-pointer border-gray-200 hover:bg-gray-50'}`}
                            >
                                {avatar(p)}
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-sm font-semibold text-gray-800">
                                        {[p.name, p.surname].filter(Boolean).join(' ').trim()}
                                    </span>
                                    <span className="flex flex-wrap items-center gap-1.5 text-[11px] text-gray-500">
                                        <span>{kind}{p.relationship ? ` · ${p.relationship}` : ''}</span>
                                        {/* Read-only is worth knowing BEFORE switching,
                                            not after a page refuses. */}
                                        {!isOwner && p.permission === 'read' && (
                                            <span className="inline-flex items-center gap-0.5 rounded bg-gray-100 px-1 py-px text-[9px] font-semibold text-gray-500">
                                                <Eye className="h-2.5 w-2.5" />{t('family.read_only', 'Read only')}
                                            </span>
                                        )}
                                    </span>
                                </span>
                                {switching === p.patientId
                                    ? <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-[#4ABDBD]" />
                                    : isActive
                                        ? <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-[#4ABDBD] px-2 py-0.5 text-[10px] font-bold text-white">
                                              <Check className="h-2.5 w-2.5" />{t('family.viewing', 'Viewing')}
                                          </span>
                                        : p.hasLogin && <BadgeCheck className="h-4 w-4 flex-shrink-0 text-[#4ABDBD]" />}
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* ---------- SECONDARY ACCOUNTS ---------- */}
            <section className={card} data-testid="dep-secondary">
                <div className={sectionHead}>
                    <Users className="h-4 w-4 text-[#4ABDBD]" />
                    <h3 className="flex-1 text-sm font-bold text-[#1a365d]">
                        {t('family.dep_secondary_title', 'Secondary accounts')}
                    </h3>
                    {enabled && (
                        <button type="button" data-testid="dep-add-secondary"
                            onClick={() => setAddOpen(true)}
                            className="inline-flex cursor-pointer items-center gap-1 rounded-lg bg-[#4ABDBD] px-2.5 py-1.5 text-[11px] font-semibold text-white transition hover:bg-[#3AA8A8]">
                            <Plus className="h-3 w-3" />
                            {t('family.add_secondary', 'Add secondary patient')}
                        </button>
                    )}
                </div>

                <p className="px-5 pt-3 text-[11px] leading-relaxed text-gray-500">
                    {t('family.dep_secondary_note',
                        'People who belong to this account and cannot sign in — a child, or a parent you manage. You reach them by switching profile. Splitting gives them their own sign-in and they become a separate account.')}
                </p>

                <div className="flex flex-col gap-1.5 px-5 py-3">
                    {loading && <Loader2 className="mx-auto h-4 w-4 animate-spin text-[#4ABDBD]" />}

                    {!loading && secondaries.length === 0 && (
                        <p className="rounded-xl bg-gray-50 px-3 py-3 text-[11px] text-gray-500">
                            {t('family.dep_no_secondary', 'No secondary accounts yet.')}
                        </p>
                    )}

                    {secondaries.map((p) => (
                        <div key={p.patientId} data-testid={`dep-sec-${p.patientId}`}
                            className="flex items-center gap-3 rounded-xl border border-gray-200 px-3 py-2.5">
                            {avatar(p)}
                            <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-semibold text-gray-800">
                                    {[p.name, p.surname].filter(Boolean).join(' ').trim()}
                                </span>
                                <span className="block text-[11px] text-gray-500">
                                    {t('family.secondary', 'Secondary')}
                                    {p.relationship ? ` · ${p.relationship}` : ''}
                                </span>
                            </span>
                            {p.canWrite && (
                                <button type="button" data-testid={`dep-split-${p.patientId}`}
                                    onClick={() => setSplitFor(p)}
                                    className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-[11px] font-semibold text-[#3AA8A8] transition hover:bg-gray-50">
                                    <Scissors className="h-3 w-3" />
                                    {t('family.split_short', 'Split')}
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* ---------- SHARING ----------
                TWO LISTS, NEVER ONE. Sharing goes one way, so "who can open my
                account" and "accounts I can open" are different facts about
                different records. Merging them would imply access is mutual. */}
            <section className={card} data-testid="dep-sharing">
                <div className={sectionHead}>
                    <Share2 className="h-4 w-4 text-[#4ABDBD]" />
                    <h3 className="flex-1 text-sm font-bold text-[#1a365d]">
                        {t('family.sharing', 'Sharing')}
                    </h3>
                </div>

                <div className="grid grid-cols-1 gap-5 px-5 py-4 md:grid-cols-2">

                    <div className="flex min-w-0 flex-col gap-2">
                        <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-gray-400">
                            <ArrowUpRight className="h-3 w-3" />
                            {t('family.can_open_mine', 'Who can open my account')}
                        </p>
                        <p className="text-[10px] leading-relaxed text-gray-400">
                            {t('family.can_open_mine_note', 'You can change or remove this at any time. Nobody has to agree.')}
                        </p>

                        {sharesLoading && <Loader2 className="mx-auto h-4 w-4 animate-spin text-[#4ABDBD]" />}

                        {!sharesLoading && (sharedWithOthers || []).length === 0 && (
                            <p className="rounded-xl bg-gray-50 px-3 py-3 text-[11px] text-gray-500">
                                {t('family.nobody_can_open', 'Nobody else can open your account.')}
                            </p>
                        )}

                        {(sharedWithOthers || []).map((row) => (
                            <div key={row.shareId} data-testid={`dep-share-${row.shareId}`}
                                className="rounded-xl border border-gray-200 p-2.5">
                                <div className="flex items-center gap-2.5">
                                    {avatar(row, 'h-8 w-8')}
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-sm font-semibold text-gray-800">{row.name}</span>
                                        <span className="block text-[10px] text-gray-400">
                                            {row.accountType === 'secondary'
                                                ? t('family.secondary', 'Secondary')
                                                : t('family.primary', 'Primary')}
                                        </span>
                                    </span>
                                </div>

                                <div className="mt-2 flex gap-1.5">
                                    {/* Two buttons, not a dropdown: the current level
                                        decides what somebody else may do to this
                                        record, so it should be readable without
                                        opening anything first. */}
                                    <button type="button" disabled={busy === row.shareId}
                                        onClick={() => row.permission !== 'read' && act(updateShare, { shareId: row.shareId, permission: 'read' }, t('family.access_updated', 'Access updated'))}
                                        className={`flex flex-1 cursor-pointer items-center justify-center gap-1 rounded-lg border py-1.5 text-[11px] font-semibold transition disabled:opacity-50 ${
                                            row.permission === 'read'
                                                ? 'border-[#4ABDBD] bg-[#4ABDBD]/10 text-[#3AA8A8]'
                                                : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                                        <Eye className="h-3 w-3" />{t('family.read', 'Read')}
                                    </button>
                                    <button type="button" disabled={busy === row.shareId}
                                        onClick={() => row.permission !== 'readwrite' && act(updateShare, { shareId: row.shareId, permission: 'readwrite' }, t('family.access_updated', 'Access updated'))}
                                        className={`flex flex-1 cursor-pointer items-center justify-center gap-1 rounded-lg border py-1.5 text-[11px] font-semibold transition disabled:opacity-50 ${
                                            row.permission === 'readwrite'
                                                ? 'border-[#4ABDBD] bg-[#4ABDBD]/10 text-[#3AA8A8]'
                                                : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                                        <Pencil className="h-3 w-3" />{t('family.read_write', 'Read & write')}
                                    </button>
                                    <button type="button" disabled={busy === row.shareId}
                                        onClick={() => setConfirming(row.shareId)}
                                        className="cursor-pointer rounded-lg border border-red-200 px-2.5 py-1.5 text-red-500 transition hover:bg-red-50 disabled:opacity-50">
                                        {busy === row.shareId ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                                    </button>
                                </div>

                                {confirming === row.shareId && (
                                    <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-2.5">
                                        <p className="text-[11px] text-red-900">
                                            {t('family.revoke_confirm', 'Remove their access? They will no longer be able to open your account.')}
                                        </p>
                                        <div className="mt-2 flex gap-2">
                                            <button type="button" data-testid={`dep-revoke-${row.shareId}`}
                                                onClick={() => act(revokeShare, row.shareId, t('family.access_removed', 'Access removed'))}
                                                className="flex-1 cursor-pointer rounded-lg bg-red-600 py-1.5 text-[10px] font-semibold text-white hover:bg-red-700">
                                                {t('family.remove', 'Remove')}
                                            </button>
                                            <button type="button" onClick={() => setConfirming(null)}
                                                className="flex-1 cursor-pointer rounded-lg border border-gray-200 bg-white py-1.5 text-[10px] font-semibold text-gray-600 hover:bg-gray-50">
                                                {t('common.cancel', 'Cancel')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="flex min-w-0 flex-col gap-2">
                        <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-gray-400">
                            <ArrowDownLeft className="h-3 w-3" />
                            {t('family.i_can_open', 'Accounts I can open')}
                        </p>
                        <p className="text-[10px] leading-relaxed text-gray-400">
                            {t('family.i_can_open_note', 'Shared with you by someone else. They decide the level — you can give it up at any time.')}
                        </p>

                        {!sharesLoading && (sharedWithMe || []).length === 0 && (
                            <p className="rounded-xl bg-gray-50 px-3 py-3 text-[11px] text-gray-500">
                                {t('family.none_shared_with_me', 'Nobody has shared their account with you.')}
                            </p>
                        )}

                        {(sharedWithMe || []).map((row) => (
                            <div key={row.shareId} className="flex items-center gap-2.5 rounded-xl border border-gray-200 p-2.5">
                                {avatar(row, 'h-8 w-8')}
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-sm font-semibold text-gray-800">{row.name}</span>
                                    <span className="block text-[10px] text-gray-400">
                                        {row.accountType === 'secondary'
                                            ? t('family.secondary', 'Secondary')
                                            : t('family.primary', 'Primary')}
                                        {' · '}
                                        {row.permission === 'readwrite'
                                            ? t('family.read_write', 'Read & write')
                                            : t('family.read_only', 'Read only')}
                                    </span>
                                </span>
                                {/* A SECONDARY cannot sign in, so walking away from it
                                    does not release you from someone else's record —
                                    it strands the account, or hands it to somebody
                                    else without their say. Split it first instead. */}
                                {row.accountType === 'secondary' ? (
                                    <span className="rounded-lg bg-gray-50 px-2 py-1 text-[10px] font-medium text-gray-400">
                                        {t('family.managed_by_you', 'You manage this')}
                                    </span>
                                ) : (
                                    <button type="button" disabled={busy === row.shareId}
                                        onClick={() => act(revokeShare, row.shareId, t('family.access_given_up', 'You no longer have access'))}
                                        className="cursor-pointer rounded-lg border border-gray-200 px-2 py-1 text-[10px] font-semibold text-gray-500 transition hover:bg-gray-50 disabled:opacity-50">
                                        {busy === row.shareId ? <Loader2 className="h-3 w-3 animate-spin" /> : t('family.give_up', 'Give up')}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <AddFamilyMember isOpen={addOpen} onClose={() => setAddOpen(false)} />

            {/* Split runs INSIDE its own card rather than as a dialog over this
                page — the same reason it lives inside the switcher rather than on
                top of it. One window whose contents change. */}
            {splitFor && (
                <section className={card} data-testid="dep-split-panel">
                    <div className={sectionHead}>
                        <Scissors className="h-4 w-4 text-[#4ABDBD]" />
                        <h3 className="flex-1 text-sm font-bold text-[#1a365d]">
                            {t('family.split_title', 'Split account')} — {[splitFor.name, splitFor.surname].filter(Boolean).join(' ').trim()}
                        </h3>
                        <button type="button" onClick={() => setSplitFor(null)}
                            className="cursor-pointer text-[11px] font-semibold text-gray-400 hover:text-gray-600">
                            {t('common.cancel', 'Cancel')}
                        </button>
                    </div>
                    <SplitMemberFlow
                        member={splitFor}
                        onDone={() => setSplitFor(null)}
                        onSplit={async (pid) => {
                            // Splitting the profile you are ACTING AS would leave the
                            // app signed in as somebody this login no longer reaches,
                            // and every screen would refuse.
                            if (primary?.patientId && pid === localStorage.getItem('ID')) {
                                try { await dispatch(switchProfile(primary.patientId)).unwrap(); } catch { /* the reload re-reads it */ }
                                setTimeout(() => window.location.reload(), 900);
                            }
                        }}
                        onChanged={() => { dispatch(getMyFamily()); dispatch(getShares()); }}
                    />
                </section>
            )}
        </div>
    );
};

export default Dependencies;
